import type { EventAttendanceMechanism, EventProgress, EventV2 } from '../types'

// What an event needs from the user next, and on which session. Shared by the index and
// detail pages so both flag the same session with the same text. At most one per event:
// pending attendance means the attempt has no attendance yet, while incomplete
// requirements mean it does.
export type EventAttention =
  | {
      kind: 'attendance'
      sessionId: string
      mechanism: EventAttendanceMechanism['kind']
      selfCheckInOpen: boolean
    }
  | { kind: 'requirements'; sessionId: string }

export let deriveEventAttention = (event: EventV2): EventAttention | null => {
  if (event.pendingAttendance != null) {
    return {
      kind: 'attendance',
      sessionId: event.pendingAttendance.sessionID,
      mechanism: event.attendanceTrackingMechanism.kind,
      selfCheckInOpen: event.pendingAttendance.selfCheckIn === 'open',
    }
  }
  if (event.incompleteSessionID != null) {
    return { kind: 'requirements', sessionId: event.incompleteSessionID }
  }
  return null
}

// Requirements text is generic: the events payload doesn't say whether the quiz or the
// survey is outstanding. The detail page refines it from /progress.
export let attentionLabel = (attention: EventAttention): string => {
  if (attention.kind === 'requirements') return 'Action required for completion'
  if (attention.mechanism === 'managerTracked')
    return 'Your manager will record attendance'
  return attention.selfCheckInOpen
    ? 'Record your attendance'
    : 'Contact your event manager to record attendance'
}

// Whether the events payload's per-user fields disagree with fresh /progress, meaning the
// events were loaded before progress last moved (quiz passed, attendance recorded by a
// manager, another tab). Used to refresh events only when needed.
//
// The API omits None fields rather than sending null, so an absent incompleteSessionID or
// pendingAttendance means "none" once ENG-979 is deployed; completedSessionIDs (always sent,
// possibly empty) marks that. An absent pendingAttendance stays ambiguous until ENG-984
// ships, so a progress-side pending with no events-side attention isn't treated as stale.
export let isEventAttentionStale = (
  event: EventV2,
  progress: EventProgress,
): boolean => {
  if (event.completedSessionIDs === undefined) return false
  let actual = deriveEventAttention(event)
  let { attendance, completion } = progress

  if (attendance.kind === 'pending') {
    if (actual?.kind === 'requirements') return true
    if (actual?.kind === 'attendance')
      return (
        actual.sessionId !== attendance.sessionID ||
        actual.selfCheckInOpen !== attendance.selfCheckInOpen
      )
    return false
  }
  if (attendance.kind === 'recorded' && completion.kind === 'notCompleted')
    return actual?.kind !== 'requirements'
  if (completion.kind === 'completed' && event.completedSessionIDs.length === 0)
    return true
  return actual != null
}
